import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  QueryAndOutputs,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentCodeParams
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class TextToColumns extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.texttocolumns',
        group: 'Data Preparation',
        title: 'Text to columns',
        description: `
      This component adds new columns based on spliting the text string in a text column.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'col',
          type: WorkflowComponentParamType.Column,
          title: 'Column to split',
          parent: 'source',
          dataType: [ColumnDataType.String]
        },
        {
          name: 'delimiters',
          type: WorkflowComponentParamType.String,
          title: 'Delimiters',
          default: ',',
          allowExpressions: false
        },
        {
          name: 'prefix',
          type: WorkflowComponentParamType.String,
          title: 'Prefix for new column names',
          optional: true,
          showIf: [
            {
              parameter: 'mode',
              value: 'Split to columns'
            }
          ],
          allowExpressions: false
        },
        {
          name: 'mode',
          type: WorkflowComponentParamType.Selection,
          title: 'Mode',
          options: ['Split to columns', 'Split to rows'],
          default: 'Split to columns'
        },
        {
          name: 'numcols',
          type: WorkflowComponentParamType.Number,
          title: 'Number of columns',
          min: 2,
          default: 3,
          mode: WorkflowComponentParameterMode.Slider,
          showIf: [
            {
              parameter: 'mode',
              value: 'Split to columns'
            }
          ]
        },
        {
          name: 'extracharacters',
          type: WorkflowComponentParamType.Selection,
          title: 'Extra characters',
          options: ['Leave in last column', 'Drop'],
          showIf: [
            {
              parameter: 'mode',
              value: 'Split to columns'
            }
          ],
          default: 'Leave in last column'
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    let query = ''
    const newcolprefix = params.prefix ? params.prefix : params.col
    const numcols = params.numcols != null ? Math.floor(params.numcols as number) : 3
    switch (connection.type) {
      case Provider.BigQuery: {
        let select = ''
        const split = dedent`WITH __split AS
                      (
                        SELECT
                          ${params.col},
                          REGEXP_EXTRACT_ALL(
                            ${params.col},
                            r'([^${params.delimiters}]+)'
                          ) AS arr
                        FROM (
                          SELECT DISTINCT ${params.col}
                          FROM ${connection.quoteName(params.source as string)}
                        )
                      )`
        if (params.mode === 'Split to rows') {
          select = dedent`${split},
                        __unnested AS
                        (
                          SELECT
                            __split.${params.col},
                            __tokens
                          FROM
                            __split,
                            UNNEST(arr) AS __tokens
                        )
                        SELECT
                          b.* EXCEPT (${params.col}),
                          a.__tokens AS ${params.col}
                        FROM
                          __unnested a,
                          ${connection.quoteName(params.source as string)} b
                        WHERE
                          a.${params.col} = b.${params.col}`
        } else {
          let newcols = ''
          if (params.extracharacters === 'Leave in last column') {
            const cols = [...Array(numcols - 1).keys()].map(
              (x) => `a.arr[SAFE_OFFSET(${x})] AS ${newcolprefix}_${x + 1}`
            )
            cols.push(dedent`SUBSTR(
                  a.${params.col},
                  ${[...Array(numcols - 1).keys()]
                    .map((x) => `LENGTH(COALESCE(a.arr[SAFE_OFFSET(${x})], ''))`)
                    .join(' + ')} + ${numcols}
                ) AS ${newcolprefix}_${numcols}`)
            newcols = cols.join(',\n')
          } else {
            newcols = [...Array(numcols).keys()]
              .map((x) => `a.arr[SAFE_OFFSET(${x})] AS ${params.col}_${x + 1}`)
              .join(',\n')
          }
          select = dedent`${split}
                            SELECT
                              b.*,
                              ${dedent(newcols)}
                            FROM
                              __split a,
                              ${connection.quoteName(params.source as string)} b
                            WHERE
                              a.${params.col} = b.${params.col}`
        }
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Snowflake: {
        let select = ''
        const split = dedent`WITH __split AS
                      (
                        SELECT
                          ${params.col},
                          REGEXP_SUBSTR_ALL(
                            ${params.col},
                            '([^${params.delimiters}]+)'
                          ) AS arr
                        FROM (
                          SELECT DISTINCT ${params.col}
                          FROM ${connection.quoteName(params.source as string)}
                        )
                      )`
        if (params.mode === 'Split to rows') {
          select = dedent`${split},
                        __unnested AS
                        (
                          SELECT
                            __split.${params.col},
                            __tokens.value AS __token
                          FROM
                            __split,
                            LATERAL FLATTEN(input => arr) __tokens
                        )
                        SELECT
                          b.* EXCLUDE (${params.col}),
                          a.__token AS ${params.col}
                        FROM
                          __unnested a,
                          ${connection.quoteName(params.source as string)} b
                        WHERE
                          a.${params.col} = b.${params.col}`
        } else {
          let newcols = ''
          if (params.extracharacters === 'Leave in last column') {
            const cols = [...Array(numcols - 1).keys()].map((x) => `a.arr[${x}] AS ${newcolprefix}_${x + 1}`)
            cols.push(dedent`SUBSTR(
                      a.${params.col},
                      ${[...Array(numcols - 1).keys()]
                        .map((x) => `LENGTH(COALESCE(a.arr[${x}], ''))`)
                        .join(' + ')} + ${numcols}
                    ) AS ${newcolprefix}_${numcols}`)
            newcols = cols.join(',\n')
          } else {
            newcols = [...Array(numcols).keys()].map((x) => `a.arr[${x}] AS ${params.col}_${x + 1}`).join(',\n')
          }
          select = dedent`${split}
                            SELECT
                              b.*,
                              ${dedent(newcols)}
                            FROM
                              __split a,
                              ${connection.quoteName(params.source as string)} b
                            WHERE
                              a.${params.col} = b.${params.col}`
        }
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
      case Provider.Redshift: {
        let select = ''
        if (params.mode === 'Split to rows') {
          const select = dedent`WITH __ns AS (
                            SELECT 1 AS N UNION ALL
                            SELECT 2 UNION ALL
                            SELECT 3 UNION ALL
                            SELECT 4 UNION ALL
                            SELECT 5 UNION ALL
                            SELECT 6 UNION ALL
                            SELECT 7 UNION ALL
                            SELECT 8 UNION ALL
                            SELECT 9 UNION ALL
                            SELECT 10
                          )
                          SELECT
                            a.*,
                            REGEXP_SUBSTR(
                              a.${params.col},
                              '[^${params.delimiters}]+',
                              1,
                              __ns.n
                            ) AS __${params.col}
                          FROM
                            __ns,
                            ${connection.quoteName(params.source as string)} a
                          WHERE
                            __ns.n <= REGEXP_COUNT(
                              a.${params.col},
                              '[^${params.delimiters}]+'
                            )`
          query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
          query +=
            '\n' +
            dedent`ALTER TABLE ${outputTablename}
                    DROP COLUMN ${params.col};
                    ALTER TABLE ${outputTablename}
                    RENAME COLUMN __${params.col} TO ${params.col};`
        } else {
          let newcols = ''
          if (params.extracharacters === 'Leave in last column') {
            const cols = [...Array(numcols - 1).keys()].map(
              (x) => dedent`
              REGEXP_SUBSTR(
                a.${params.col},
                '[^${params.delimiters}]+',
                1,
                ${x + 1}
              ) AS ${newcolprefix}_${x + 1}`
            )
            cols.push(dedent`SUBSTRING(
                      a.${params.col},
                      ${[...Array(numcols - 1).keys()]
                        .map(
                          (x) => dedent`LENGTH(COALESCE(
                          REGEXP_SUBSTR(
                            a.${params.col},
                            '[^${params.delimiters}]+',
                            1,
                            ${x + 1}
                          ),
                          ''
                        ))`
                        )
                        .join('\n+\n')} + ${numcols}
                    ) AS ${newcolprefix}_${numcols}`)

            newcols = cols.join(',\n')
          } else {
            const cols = [...Array(numcols).keys()].map(
              (x) => dedent`
                REGEXP_SUBSTR(
                  a.${params.col},
                  '[^${params.delimiters}]+',
                  1,
                  ${x + 1}
                ) AS ${newcolprefix}_${x + 1}`
            )
            newcols = cols.join(',\n')
          }
          select = dedent`SELECT
                            *,
                            ${dedent(newcols)}
                          FROM ${connection.quoteName(params.source as string)} a`
          query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        }
        break
      }
      case Provider.Postgres: {
        // NOTE: The Redshift code should work for Postgres >= 15, where the REGEXP_SUBSTR function is available
        let select = ''
        const split = dedent`WITH __split AS
                      (
                        SELECT
                          a.${params.col},
                          REGEXP_SPLIT_TO_ARRAY(
                            a.${params.col},
                            '[${params.delimiters}]'
                          ) AS arr
                        FROM (
                          SELECT DISTINCT ${params.col}
                          FROM ${connection.quoteName(params.source as string)}
                        ) a
                      )`
        if (params.mode === 'Split to rows') {
          select = dedent`${split},
                        __unnested AS
                        (
                          SELECT
                            __split.${params.col},
                            __tokens
                          FROM
                            __split,
                            UNNEST(arr) AS __tokens
                        )
                        SELECT
                          b.*,
                          a.__tokens AS __tokens
                        FROM
                          __unnested a,
                          ${connection.quoteName(params.source as string)} b
                        WHERE
                          a.${params.col} = b.${params.col}`
        } else {
          let newcols = ''
          if (params.extracharacters === 'Leave in last column') {
            const cols = [...Array(numcols - 1).keys()].map((x) => `a.arr[${x + 1}] AS ${newcolprefix}_${x + 1}`)
            cols.push(dedent`SUBSTR(
                  a.${params.col},
                  ${[...Array(numcols - 1).keys()]
                    .map((x) => `LENGTH(COALESCE(a.arr[${x + 1}], ''))`)
                    .join(' + ')} + ${numcols}
                ) AS ${newcolprefix}_${numcols}`)
            newcols = cols.join(',\n')
          } else {
            newcols = [...Array(numcols).keys()].map((x) => `a.arr[${x + 1}] AS ${params.col}_${x + 1}`).join(',\n')
          }
          select = dedent`${split}
                            SELECT
                              b.*,
                              ${dedent(newcols)}
                            FROM
                              __split a,
                              ${connection.quoteName(params.source as string)} b
                            WHERE
                              a.${params.col} = b.${params.col}`
        }
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        if (params.mode === 'Split to rows') {
          query +=
            '\n' +
            dedent`ALTER TABLE ${outputTablename}
                    DROP COLUMN ${params.col};
                    ALTER TABLE ${outputTablename}
                    RENAME COLUMN __tokens TO ${params.col};`
        }
        break
      }
      case Provider.DatabricksRest: {
        let select = ''
        const split = dedent`WITH __split AS
                      (
                        SELECT /*+ REPARTITION(40) */
                          ${params.col},
                          REGEXP_EXTRACT_ALL(
                            ${params.col},
                            r'([^${params.delimiters}]+)'
                          ) AS arr
                        FROM (
                          SELECT /*+ REPARTITION(40) */
                            DISTINCT ${params.col}
                          FROM ${connection.quoteName(params.source as string)}
                        )
                      )`
        if (params.mode === 'Split to rows') {
          select = dedent`${split},
                        __unnested AS
                        (
                          SELECT /*+ REPARTITION(40) */
                            __split.${params.col},
                            EXPLODE(arr) AS __tokens
                          FROM
                            __split
                        )
                        SELECT /*+ REPARTITION(40) */
                          b.* EXCEPT (${params.col}),
                          a.__tokens AS ${params.col}
                        FROM
                          __unnested a,
                          ${connection.quoteName(params.source as string)} b
                        WHERE
                          a.${params.col} = b.${params.col}`
        } else {
          let newcols = ''
          if (params.extracharacters === 'Leave in last column') {
            const cols = [...Array(numcols - 1).keys()].map((x) => `a.arr[${x}] AS ${newcolprefix}_${x + 1}`)
            cols.push(dedent`SUBSTR(
                  a.${params.col},
                  ${[...Array(numcols - 1).keys()]
                    .map((x) => `LENGTH(COALESCE(a.arr[${x}], ''))`)
                    .join(' + ')} + ${numcols}
                ) AS ${newcolprefix}_${numcols}`)
            newcols = cols.join(',\n')
          } else {
            newcols = [...Array(numcols).keys()].map((x) => `a.arr[${x}] AS ${params.col}_${x + 1}`).join(',\n')
          }
          select = dedent`${split}
                            SELECT /*+ REPARTITION(40) */
                              b.*,
                              ${dedent(newcols)}
                            FROM
                              __split a,
                              ${connection.quoteName(params.source as string)} b
                            WHERE
                              a.${params.col} = b.${params.col}`
        }
        query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        break
      }
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { TextToColumns }
